/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import I18n from 'I18n';
import { createContext, useEffect, useMemo, useContext, useCallback, useState } from 'react';
import { useFullPlaylist, useRemoveFromPlaylist, useUpdatePlaylist } from '../../savedItems/savedItemsQueries.migrated';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import produce from 'immer';
import { jsx as _jsx } from "react/jsx-runtime";
const swapArrayItems = (items, fromIndex, toIndex) => {
  const newItems = [...items];
  newItems.splice(toIndex, 0, newItems.splice(fromIndex, 1)[0]);
  return newItems;
};
const noop = () => {};
const useLocalDraft = value => {
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (value && draft === null) {
      setDraft(value);
    }
  }, [draft, value]);
  return [draft || [], setDraft];
};
const PlaylistItemManagerContext = /*#__PURE__*/createContext({
  playlistId: 0,
  playlistItems: [],
  addPlaylistItem: noop,
  removePlaylistItem: noop,
  movePlaylistItem: noop,
  saveNewPlaylistItems: () => Promise.resolve({}),
  updatePlaylistLoading: false
});
export const usePlaylistItemManager = () => {
  const state = useContext(PlaylistItemManagerContext);
  return Object.assign({}, state, {
    numberOfSelectedItems: state.playlistItems.filter(item => !item.hasBeenRemoved).length
  });
};
export const PlaylistItemManager = ({
  playlistId,
  render
}) => {
  const {
    playlist
  } = useFullPlaylist(playlistId);
  const [playlistDraft, setPlaylistDraft] = useLocalDraft((playlist === null || playlist === void 0 ? void 0 : playlist.items) || null);
  const [removeFromPlaylist] = useRemoveFromPlaylist();
  const [updatePlaylist, {
    updatePlaylistMutationLoading
  }] = useUpdatePlaylist(playlistId, {
    onError() {
      setPlaylistDraft((playlist === null || playlist === void 0 ? void 0 : playlist.items) || null);
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: I18n.text('learningCenter.savedItems.playlistDetails.playlistUpdateError')
      });
    }
  });
  const playlistItemsToRender = useMemo(() => {
    if (!playlist || !playlistDraft) return [];
    return playlistDraft.map(playlistItem => Object.assign({}, playlistItem, {
      hasBeenRemoved: playlistItem.hasBeenRemoved || !playlist.items.some(item => item.itemId === playlistItem.itemId)
    }));
  }, [playlist, playlistDraft]);
  const addPlaylistItem = useCallback(playlistItem => {
    /**
     * Rather than add the item using the dedicated endpoint, we want
     * to add it preserving the position in the playlist that it had.
     * Otherwise if the user removes an item and then adds it back, it
     * will be at the end of the playlist which is unexpected.
     */
    return updatePlaylist({
      items: playlistItemsToRender.filter(item => {
        if (item.itemId === playlistItem.itemId && item.type === playlistItem.type && item.language === playlistItem.language) {
          return true;
        }
        return !item.hasBeenRemoved;
      })
    });
  }, [playlistItemsToRender, updatePlaylist]);
  const movePlaylistItem = useCallback((fromIndex, toIndex) => {
    setPlaylistDraft(oldDraft => {
      if (!oldDraft) return null;
      const newDraft = produce(oldDraft, draft => {
        return swapArrayItems(draft, fromIndex, toIndex);
      });
      const itemsToSave = newDraft.filter(item => !item.hasBeenRemoved);
      updatePlaylist({
        items: itemsToSave
      });
      return newDraft;
    });
  }, [setPlaylistDraft, updatePlaylist]);
  const removePlaylistItem = useCallback(playlistItem => {
    const isTargetItem = item => item.itemId === playlistItem.itemId && item.type === playlistItem.type && item.language === playlistItem.language;
    const setHasBeenRemoved = value => {
      setPlaylistDraft(currentDraft => {
        if (!currentDraft) return null;
        return currentDraft.map(item => isTargetItem(item) ? Object.assign({}, item, {
          hasBeenRemoved: value
        }) : item);
      });
    };
    setHasBeenRemoved(true);
    return removeFromPlaylist(Object.assign({
      playlistId
    }, playlistItem)).catch(() => {
      setHasBeenRemoved(false);
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: I18n.text('learningCenter.savedItems.tooltips.removeFromPlaylistError')
      });
    });
  }, [playlistId, removeFromPlaylist, setPlaylistDraft]);
  const saveNewPlaylistItems = useCallback(items => {
    const newItems = items.filter(item => !item.hasBeenRemoved);
    return updatePlaylist({
      items: newItems.map(item => ({
        itemId: item.itemId,
        type: item.type,
        language: item.language
      }))
    }).then(result => {
      setPlaylistDraft(newItems);
      return result;
    });
  }, [setPlaylistDraft, updatePlaylist]);
  const contextState = useMemo(() => ({
    playlistId,
    playlistItems: playlistItemsToRender,
    addPlaylistItem,
    removePlaylistItem,
    movePlaylistItem,
    saveNewPlaylistItems,
    updatePlaylistLoading: updatePlaylistMutationLoading
  }), [addPlaylistItem, movePlaylistItem, playlistId, playlistItemsToRender, removePlaylistItem, saveNewPlaylistItems, updatePlaylistMutationLoading]);
  return /*#__PURE__*/_jsx(PlaylistItemManagerContext.Provider, {
    value: contextState,
    children: render(playlistItemsToRender)
  });
};